import type { CoachingPartnership } from '@efdigital/ll-addon-data';
import type { CoachingPlanner } from '@efdigital/ll-xceed-v2/utils/coaching/coachee/coaching-plan-manager';
import {
  markPromptedForFeedback,
  shouldAutoPromptForFeedback,
} from '@efdigital/ll-xceed-v2/utils/coaching/coachee/feedback-prompt';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import BsModal from 'ember-bootstrap/components/bs-modal';

const RATINGS = [1, 2, 3, 4, 5] as const;

interface GiveFeedbackModalSignature {
  Args: {
    partnership: CoachingPartnership;
    planner: CoachingPlanner;
    coachName: string;
    /**
     * Open the modal by itself on first render, at most once per browser
     * session per partnership (see `feedback-prompt.ts`). Closing it leaves
     * the yielded trigger in place to reopen it.
     */
    autoOpen?: boolean;
    onChanged?: () => void | Promise<void>;
  };
  Blocks: {
    default: [showModal: () => void];
  };
}

/**
 * Feedback form for a concluded coaching partnership.
 *
 * One submission per partnership — ll-api enforces this with a 409, which the
 * planner treats as done: a coachee reaching this modal a second time (a stale
 * tab, a double-click) already has their answer on record, so the modal just
 * closes and the banner goes away.
 *
 * A successful submit hides the banner, which unmounts this component while
 * the modal is open. That is safe: `BsModal` removes the body `modal-open`
 * class and scrollbar padding in `willDestroy`, and its backdrop is part of
 * its own template.
 *
 * With `@autoOpen`, the initial `open` state is derived from the args and
 * whether this partnership has already been prompted; the prompt is only
 * recorded once `BsModal` actually shows, via `@onShow`.
 */
export class GiveFeedbackModal extends Component<GiveFeedbackModalSignature> {
  @tracked open =
    Boolean(this.args.autoOpen) &&
    shouldAutoPromptForFeedback(this.args.partnership.id!);
  @tracked loading = false;
  @tracked rating: number | null = null;
  @tracked comment = '';
  @tracked failed = false;

  markPrompted = () => {
    markPromptedForFeedback(this.args.partnership.id!);
  };

  closeModal = () => {
    this.open = false;
  };

  showModal = () => {
    this.rating = null;
    this.comment = '';
    this.failed = false;
    this.open = true;
  };

  selectRating = (value: number) => {
    this.rating = value;
  };

  updateComment = (event: Event) => {
    this.comment = (event.target as HTMLTextAreaElement).value;
  };

  get submitDisabled(): boolean {
    return this.loading || this.rating === null;
  }

  submitFeedback = async () => {
    if (this.rating === null) {
      return;
    }

    this.loading = true;
    this.failed = false;
    try {
      await this.args.planner.giveFeedback({
        rating: this.rating,
        ...(this.comment.trim() ? { comment: this.comment.trim() } : {}),
      });
      this.open = false;
      await this.args.onChanged?.();
    } catch (error) {
      console.error(error);
      this.failed = true;
    } finally {
      this.loading = false;
    }
  };

  ratingIsSelected = (value: number): boolean => this.rating === value;

  <template>
    {{#if this.open}}
      <BsModal
        @open={{this.open}}
        @onShow={{this.markPrompted}}
        @onHidden={{this.closeModal}}
        data-test-give-feedback-modal
        as |modal|
      >
        <modal.header>
          <h5 class="modal-title">Rate your coaching with {{@coachName}}</h5>
        </modal.header>
        <modal.body>
          <p>How would you rate your coaching overall?</p>
          <div
            class="d-flex gap-2 mb-3"
            role="radiogroup"
            aria-label="Rating out of 5"
          >
            {{#each RATINGS as |value|}}
              <div class="form-check form-check-inline m-0">
                <input
                  class="form-check-input"
                  type="radio"
                  name="feedback-rating"
                  id="feedback-rating-{{value}}"
                  checked={{this.ratingIsSelected value}}
                  {{on "change" (fn this.selectRating value)}}
                />
                <label
                  class="form-check-label"
                  for="feedback-rating-{{value}}"
                >{{value}}</label>
              </div>
            {{/each}}
          </div>
          <div class="mb-3">
            <label class="form-label" for="feedback-comment">
              Anything you'd like to add? (optional)
            </label>
            <textarea
              id="feedback-comment"
              class="form-control"
              rows="4"
              value={{this.comment}}
              placeholder="What worked well, what could be different..."
              {{on "input" this.updateComment}}
            ></textarea>
          </div>
          <p class="text-body-secondary small mb-0">
            Your feedback will be kept anonymous.
          </p>
          {{#if this.failed}}
            <p class="text-danger small mt-2 mb-0">
              Something went wrong submitting your feedback. Please try again.
            </p>
          {{/if}}
        </modal.body>
        <modal.footer>
          <button
            class="btn btn-outline-primary"
            type="button"
            {{on "click" this.closeModal}}
            disabled={{this.loading}}
          >Cancel</button>
          <button
            class="btn btn-primary"
            type="button"
            {{on "click" this.submitFeedback}}
            disabled={{this.submitDisabled}}
          >Submit feedback</button>
        </modal.footer>
      </BsModal>
    {{/if}}
    {{yield this.showModal}}
  </template>
}

export default GiveFeedbackModal;
